import { useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Wallet, TrendingUp, TrendingDown, X } from 'lucide-react'
import { accountApi, positionsApi } from '../api/client'
import { useWebSocketEvent } from '../hooks/useWebSocket'
import type { WebSocketHookReturn } from '../hooks/useWebSocket'
import './AccountPanel.css'

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}
const fmt = (v: unknown, d = 2): string => num(v).toFixed(d)
const fmtUSD = (v: unknown): string => `$${num(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtSignedUSD = (v: unknown): string => {
  const n = num(v)
  if (n === 0) return '$0.00'
  return `${n > 0 ? '+' : '−'}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

interface AccountPanelProps {
  ws: WebSocketHookReturn
}

export function AccountPanel({ ws }: AccountPanelProps) {
  const queryClient = useQueryClient()

  const { data: account, isError: accountError } = useQuery({
    queryKey: ['account'],
    // Wrapped, NOT passed bare: accountApi.get now takes an optional
    // accountId, and react-query calls queryFn with a QueryFunctionContext —
    // a bare reference would forward that object as the account selector.
    queryFn: () => accountApi.get(),
    refetchInterval: 10000,
  })

  const { data: positions, isLoading } = useQuery({
    queryKey: ['positions'],
    queryFn: () => positionsApi.getAll(),
    refetchInterval: 10000,
  })

  const closeMutation = useMutation({
    mutationFn: positionsApi.close,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['positions'] }),
  })

  const handlePositionUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['positions'] })
    queryClient.invalidateQueries({ queryKey: ['account'] })
  }, [queryClient])
  useWebSocketEvent(ws, 'position_update', handlePositionUpdate)

  const value = num(account?.account_value)
  const dailyPnl = num(account?.daily_pnl)
  const dayPct = value - dailyPnl > 0 ? (dailyPnl / (value - dailyPnl)) * 100 : 0
  const rows = positions ?? []

  return (
    <div className="card account-panel-card">
      <div className="card-header">
        <h3 className="card-title">
          <Wallet size={20} className="icon" />
          Account & Positions
        </h3>
        <span className={`card-badge ${dailyPnl >= 0 ? 'success' : 'error'}`}>
          {dailyPnl >= 0 ? '+' : '−'}{Math.abs(dayPct).toFixed(2)}%
        </span>
      </div>

      {accountError && (
        <div className="account-error">Account data unavailable</div>
      )}

      <div className="account-tiles">
        <div className="account-tile"><span className="tile-key">Value</span><span className="tile-val mono">{fmtUSD(account?.account_value)}</span></div>
        <div className="account-tile"><span className="tile-key">Day P&L</span><span className={`tile-val mono ${dailyPnl >= 0 ? 'positive' : 'negative'}`}>{fmtSignedUSD(account?.daily_pnl)}</span></div>
        <div className="account-tile"><span className="tile-key">Buying Power</span><span className="tile-val mono">{fmtUSD(account?.buying_power)}</span></div>
        <div className="account-tile"><span className="tile-key">Cash</span><span className="tile-val mono">{fmtUSD(account?.cash)}</span></div>
      </div>

      <div className="positions-scroll">
        {isLoading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><TrendingUp size={32} aria-hidden="true" /></div>
            <div className="empty-text">No open positions</div>
          </div>
        ) : (
          <ul className="position-rows">
            {rows.map((p) => {
              const pnl = num(p.unrealized_pnl)
              const sign = pnl >= 0 ? 'positive' : 'negative'
              return (
                <li className="position-row" key={p.symbol}>
                  <span className="pos-symbol">
                    {pnl >= 0 ? <TrendingUp size={13} className="trend positive" /> : <TrendingDown size={13} className="trend negative" />}
                    {p.symbol}
                    <span className="pos-qty mono">×{p.quantity}</span>
                  </span>
                  <span className="pos-price mono">${fmt(p.current_price)}</span>
                  <span className={`pos-pnl mono ${sign}`}>{fmtSignedUSD(p.unrealized_pnl)}</span>
                  <button
                    className="btn btn-secondary btn-sm pos-close"
                    onClick={() => closeMutation.mutate(p.symbol)}
                    disabled={closeMutation.isPending && closeMutation.variables === p.symbol}
                    title="Close position"
                  >
                    <X size={13} />
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
